import { Box, SxProps, Theme, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BenefitIcon, BenefitKey } from '../../../../lib/components/compound/dataDisplay';
import { CardWidget } from '../../../../lib/components/compound/surfaces/cardWidget';
import { InfoIcon } from '../../../../lib/icons';
import { CustomBenefitIcon } from '../../../../lib/components/compound/dataDisplay/customBenefitIcon';
import { IconEnum } from '@pxtra-client-lib-legacy/gen';
import { Badge } from '../../../../lib/components/atomic/dataDisplay/badge';
import Util from '../../../../misc/util';

export interface BenefitData {
  key: string;
  usedBudget: number;
  label?: string | null;
  icon?: IconEnum;
}

export interface Props {
  variant: 'active' | 'desired';
  benefits: BenefitData[];
  link: string;
}

export const BenefitsWidget: FC<Props> = ({ variant, benefits, link }) => {
  const { t } = useTranslation();
  const tWidget = (key: Parameters<typeof t>[0]) => {
    return t(`employeeBenefitsWidget:${key}` as Parameters<typeof t>[0]);
  };
  const tBenefit = (key: Parameters<typeof t>[0]) => {
    return t(`benefits:${key}` as Parameters<typeof t>[0]);
  };
  const tBenefitText = (key: Parameters<typeof t>[0]) => {
    return t(`benefit_info_employer:${key}` as Parameters<typeof t>[0]);
  };

  const navigate = useNavigate();

  const [selectedBenefit, setSelectedBenefit] = useState<BenefitKey>();

  const textOverflowEllipsis: SxProps<Theme> = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const handleRowActionClick = (benefit: BenefitKey) => {
    if (variant == 'active') {
      navigate(link);
    } else {
      setSelectedBenefit(benefit);
    }
  };

  const renderRow = (benefit: BenefitData, row: number) => {
    return (
      <div key={`${variant}-${row}`} className="flex justify-between w-full items-center">
        <div className="flex justify-between w-full">
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-x-3">
              {benefit.key === 'custom' ? (
                <CustomBenefitIcon
                  iconKey={benefit.icon as IconEnum}
                  size={36}
                  sx={{ float: 'left' }}
                />
              ) : (
                <BenefitIcon
                  benefitKey={benefit.key as BenefitKey}
                  size={36}
                  sx={{ float: 'left' }}
                />
              )}
              <div className="flex flex-col">
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    lineHeight: '18px',
                    ...textOverflowEllipsis
                  }}
                >
                  {benefit.label ? benefit.label : tBenefit(benefit.key)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'grey.900',
                    lineHeight: '18px',
                    ...textOverflowEllipsis
                  }}
                >
                  {t(`benefits:${benefit.key}`, { context: 'subtitle' })}
                </Typography>
              </div>
            </div>
            <Badge label={Util.formatCurrency(benefit.usedBudget)} />
          </div>
        </div>

        {variant === 'desired' && (
          <InfoIcon
            className="mx-2  text-grey-600 hover:text-primary-main"
            onClick={() => {
              handleRowActionClick(benefit.key as BenefitKey);
            }}
          />
        )}
      </div>
    );
  };

  const renderTable = () => (
    <Box
      sx={{
        overflowY: 'overlay',
        height: '216px',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}
    >
      <div className="flex flex-col gap-y-2">
        {benefits.map((benefit, row) => renderRow(benefit, row))}
      </div>
    </Box>
  );

  const renderInfo = (benefit: BenefitKey) => {
    return <Typography variant="body1">{tBenefitText(benefit)}</Typography>;
  };

  const renderBody = () => {
    if (selectedBenefit) {
      return renderInfo(selectedBenefit);
    } else if (benefits.length > 0) {
      return renderTable();
    } else {
      return <Typography sx={{ color: 'grey.100' }}>{t('template:EMPTY_TABLE')}</Typography>;
    }
  };

  const handleFooterClick = () => {
    if (selectedBenefit) {
      setSelectedBenefit(undefined);
    } else {
      navigate(link);
    }
  };

  return (
    <CardWidget
      icon={selectedBenefit ? <BenefitIcon size={36} benefitKey={selectedBenefit} /> : undefined}
      title={
        selectedBenefit
          ? tBenefit(selectedBenefit)
          : variant == 'active'
            ? tWidget('ACTIVE_TITLE')
            : tWidget('DESIRED_TITLE')
      }
      subTitle={
        selectedBenefit
          ? undefined
          : variant == 'active'
            ? tWidget('ACTIVE_SUBTITLE')
            : tWidget('DESIRED_SUBTITLE')
      }
      footerText={selectedBenefit ? t('common:BACK') : tWidget('MANAGE_BENEFITS')}
      onFooterClicked={handleFooterClick}
      sx={{ width: '42.5%' }}
    >
      {renderBody()}
    </CardWidget>
  );
};
